import React from 'react';
import { Container, Row, Col, Badge } from 'react-bootstrap';
import { FaUserMd, FaShieldAlt, FaBus, FaCar, FaHardHat, FaWrench, FaBolt, FaTruck, FaFlagCheckered, FaChevronRight, FaBuilding, FaSprayCan, FaGasPump, FaBurn, FaUtensils, FaCamera, FaGlassMartiniAlt, FaTools, FaTree, FaDice, FaFish, FaShip } from 'react-icons/fa';
import { motion } from 'framer-motion';

import RedirectModal from '../components/RedirectModal';
import SEO from '../components/SEO';
import { useState } from 'react';

import jobsData from '../data/jobsData.json';

const iconMap = {
    FaShieldAlt, FaUserMd, FaBus, FaCamera,
    FaBuilding, FaCar, FaFlagCheckered, FaBolt,
    FaTruck, FaHardHat, FaWrench, FaSprayCan,
    FaGasPump, FaBurn, FaUtensils, FaGlassMartiniAlt,
    FaTools, FaTree, FaDice, FaFish, FaShip
};

const Jobs = () => {
    const [showModal, setShowModal] = useState(false);
    const [externalLink, setExternalLink] = useState('');
    const [redirectVariant, setRedirectVariant] = useState('external');

    // Data processing with icon mapping
    const govtJobs = jobsData.govtJobs.map(job => ({ ...job, icon: iconMap[job.icon] }));
    const bizJobs = jobsData.bizJobs.map(job => ({ ...job, icon: iconMap[job.icon] }));

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
    };

    const itemVariants = {
        hidden: { opacity: 0, x: -20 },
        visible: { opacity: 1, x: 0 }
    };

    const handleVisitSite = (url, variant = 'external') => {
        setExternalLink(url);
        setRedirectVariant(variant);
        setShowModal(true);
    };

    const handleConfirmRedirect = () => {
        window.open(externalLink, '_blank', 'noopener,noreferrer');
        setShowModal(false);
    };

    return (
        <div style={{ minHeight: '100vh', paddingTop: '60px' }}>
            <SEO
                title="Civilian & Government Jobs"
                description="Explore job opportunities in Thalai Nagaram RP (TNRP): Government roles (TNPD Police, EMS, Law Corps) and civilian businesses (Mechanic, Taxi, Fuel Station, Private Enterprise)."
                keywords="TNRP Jobs, Thalai Nagaram RP Careers, Tamil FiveM Police Job, EMS FiveM Tamil, GTA 5 Roleplay Careers, FiveM Government Jobs Tamil"
                canonicalUrl="https://thalainagaram.org/jobs"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "ItemList",
                    "name": "Thalai Nagaram RP Careers & Jobs",
                    "description": "Available government and civilian jobs in Thalai Nagaram Roleplay FiveM server.",
                    "itemListElement": [
                        { "@type": "ListItem", "position": 1, "name": "TNPD - Tamil Nadu Police Department" },
                        { "@type": "ListItem", "position": 2, "name": "Emergency Medical Services (EMS)" },
                        { "@type": "ListItem", "position": 3, "name": "Mechanic & Customs" },
                        { "@type": "ListItem", "position": 4, "name": "Civilian Businesses & Enterprise" }
                    ]
                }}
            />
            <RedirectModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                handleConfirm={handleConfirmRedirect}
                targetUrl={externalLink}
                variant={redirectVariant}
            />

            <Container fluid className="py-5">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-5 pb-4 d-flex align-items-end justify-content-between border-bottom"
                    style={{ borderColor: 'rgba(255, 215, 0, 0.2)' }}
                >
                    <div>
                        <h6 className="text-secondary letter-spacing-2 mb-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>CAREER OPPORTUNITIES</h6>
                        <h1 className="display-4 fw-bold text-white mb-0" style={{ fontSize: '2rem' }}>JOB <span className="text-neon-cyan">CENTER</span></h1>
                    </div>
                </motion.div>

                {/* Govt Section */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="mb-5"
                >
                    <h3 className="h5 text-white mb-4 d-flex align-items-center" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                        <span className="me-2" style={{ width: '8px', height: '8px', background: 'var(--neon-cyan)', borderRadius: '0', boxShadow: '0 0 10px var(--neon-cyan)' }}></span>
                        GOVERNMENT DEPARTMENTS
                    </h3>

                    <Row className="g-3">
                        {govtJobs.map((job, idx) => (
                            <Col lg={3} md={6} key={idx}>
                                <motion.div
                                    variants={itemVariants}
                                    className="h-100"
                                    whileHover={{ y: -5 }}
                                >
                                    <motion.div
                                        className="h-100 p-4 position-relative overflow-hidden d-flex flex-column"
                                        style={{
                                            background: 'rgba(25, 25, 25, 0.4)',
                                            backdropFilter: 'blur(10px)',
                                            border: '1px solid rgba(255, 255, 255, 0.05)',
                                            borderRadius: '20px',
                                            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
                                        }}
                                        whileHover={{
                                            borderColor: job.color,
                                            boxShadow: `0 0 25px ${job.color}30`
                                        }}
                                    >
                                        <div
                                            className="position-absolute top-0 end-0 p-3"
                                            style={{
                                                opacity: 0.05,
                                                transform: 'translate(20%, -20%) rotate(15deg) scale(4)',
                                                color: job.color,
                                                pointerEvents: 'none'
                                            }}
                                        >
                                            <job.icon />
                                        </div>

                                        <div className="d-flex align-items-center mb-4 position-relative z-1">
                                            <div
                                                className="d-flex align-items-center justify-content-center me-3 shadow-lg"
                                                style={{
                                                    width: '50px',
                                                    height: '50px',
                                                    borderRadius: '14px',
                                                    background: 'rgba(0, 0, 0, 0.6)',
                                                    border: `1px solid ${job.color}`,
                                                    boxShadow: `0 0 15px ${job.color}20`,
                                                    color: job.color
                                                }}
                                            >
                                                <job.icon size={22} />
                                            </div>
                                            <div>
                                                <Badge bg="transparent" className="text-uppercase border-0 px-0 fw-bold mb-1 d-block text-start" style={{ color: job.color, letterSpacing: '2px', fontSize: '0.65rem' }}>
                                                    {job.status}
                                                </Badge>
                                                <h6 className="m-0 text-white small opacity-75" style={{ fontFamily: 'Orbitron, sans-serif' }}>{job.id}</h6>
                                            </div>
                                        </div>

                                        <h3 className="h5 fw-bold text-white mb-3 position-relative z-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                                            {job.title}
                                        </h3>

                                        <p className="text-secondary small mb-4 position-relative z-1 flex-grow-1" style={{ lineHeight: 1.6 }}>{job.desc}</p>

                                        <div className="mt-auto position-relative z-1">
                                            {job.site && job.site !== 'N/A' && (
                                                <motion.div
                                                    className="d-inline-flex align-items-center small px-3 py-2 rounded-pill"
                                                    style={{
                                                        background: `linear-gradient(90deg, ${job.color}20, transparent)`,
                                                        border: `1px solid ${job.color}40`,
                                                        color: job.color,
                                                        cursor: 'pointer',
                                                        fontWeight: 500
                                                    }}
                                                    whileHover={{ scale: 1.05, background: `linear-gradient(90deg, ${job.color}40, transparent)` }}
                                                    onClick={() => handleVisitSite(job.site, 'official')}
                                                >
                                                    Visit Site <FaChevronRight className="ms-2" size={10} />
                                                </motion.div>
                                            )}
                                        </div>
                                    </motion.div>
                                </motion.div>
                            </Col>
                        ))}
                    </Row>
                </motion.div>

                {/* Business Section */}
                <motion.div
                    variants={containerVariants}
                    initial="visible"
                    animate="visible"
                >
                    <h3 className="h5 text-white mb-4 d-flex align-items-center" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                        <span className="me-2" style={{ width: '8px', height: '8px', background: 'var(--neon-pink)', borderRadius: '0', boxShadow: '0 0 10px var(--neon-pink)' }}></span>
                        PRIVATE SECTOR
                    </h3>

                    <Row className="g-3">
                        {bizJobs.map((job, idx) => (
                            <Col lg={3} md={6} key={idx}>
                                <motion.div
                                    variants={itemVariants}
                                    className="h-100"
                                    whileHover={{ y: -5 }}
                                >
                                    <motion.div
                                        className="h-100 p-4 position-relative overflow-hidden d-flex flex-column"
                                        style={{
                                            background: 'rgba(25, 25, 25, 0.4)',
                                            backdropFilter: 'blur(10px)',
                                            border: '1px solid rgba(255, 255, 255, 0.05)',
                                            borderRadius: '20px',
                                            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
                                        }}
                                        whileHover={{
                                            borderColor: job.color,
                                            boxShadow: `0 0 25px ${job.color}30`
                                        }}
                                    >
                                        <div
                                            className="position-absolute top-0 end-0 p-3"
                                            style={{
                                                opacity: 0.05,
                                                transform: 'translate(20%, -20%) rotate(15deg) scale(4)',
                                                color: job.color,
                                                pointerEvents: 'none'
                                            }}
                                        >
                                            <job.icon />
                                        </div>

                                        <div className="d-flex align-items-center mb-4 position-relative z-1">
                                            <div
                                                className="d-flex align-items-center justify-content-center me-3 shadow-lg"
                                                style={{
                                                    width: '50px',
                                                    height: '50px',
                                                    borderRadius: '14px',
                                                    background: 'rgba(0, 0, 0, 0.6)',
                                                    border: `1px solid ${job.color}`,
                                                    boxShadow: `0 0 15px ${job.color}20`,
                                                    color: job.color
                                                }}
                                            >
                                                <job.icon size={22} />
                                            </div>
                                            <div>
                                                <Badge bg="transparent" className="text-uppercase border-0 px-0 fw-bold mb-1 d-block text-start" style={{ color: job.color, letterSpacing: '2px', fontSize: '0.65rem' }}>
                                                    {job.status}
                                                </Badge>
                                                <h6 className="m-0 text-white small opacity-75" style={{ fontFamily: 'Orbitron, sans-serif' }}>{job.type}</h6>
                                            </div>
                                        </div>

                                        <h3 className="h5 fw-bold text-white mb-3 position-relative z-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                                            {job.title}
                                        </h3>

                                        <p className="text-secondary small mb-4 position-relative z-1 flex-grow-1" style={{ lineHeight: 1.6 }}>
                                            {job.type} Services
                                        </p>

                                        <div className="mt-auto position-relative z-1">
                                            {job.site && job.site !== 'N/A' && (
                                                <motion.div
                                                    className="d-inline-flex align-items-center small px-3 py-2 rounded-pill"
                                                    style={{
                                                        background: `linear-gradient(90deg, ${job.color}20, transparent)`,
                                                        border: `1px solid ${job.color}40`,
                                                        color: job.color,
                                                        cursor: 'pointer',
                                                        fontWeight: 500
                                                    }}
                                                    whileHover={{ scale: 1.05, background: `linear-gradient(90deg, ${job.color}40, transparent)` }}
                                                    onClick={() => handleVisitSite(job.site, 'external')}
                                                >
                                                    Visit Site <FaChevronRight className="ms-2" size={10} />
                                                </motion.div>
                                            )}
                                        </div>
                                    </motion.div>
                                </motion.div>
                            </Col>
                        ))}
                    </Row>
                </motion.div>


            </Container>
        </div>
    );
};

export default Jobs;
